import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Send, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { MemoryCard } from "@/components/MemoryCard";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const SUGGESTIONS = ["Onde coloquei meu passaporte?", "O que está emprestado?", "Quanto paguei na última revisão?", "Qual o tamanho do tênis da minha filha?"];

export default function Ask() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [q, setQ] = useState("");
  const [msgs, setMsgs] = useState([]);
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  const ask = async (question) => {
    const text = (question || q).trim();
    if (!text || busy) return;
    setQ("");
    setMsgs((m) => [...m, { role: "user", text }]);
    setBusy(true);
    try {
      const { data } = await api.post("/ai/ask", { question: text });
      setMsgs((m) => [...m, { role: "ai", ...data }]);
    } catch (e) {
      toast.error(errorMessage(e));
      setMsgs((m) => [...m, { role: "ai", answer: "Não consegui responder agora. Tente novamente em instantes.", memories: [], related: [], follow_ups: [] }]);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { const initial = params.get("q"); if (initial) ask(initial); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, busy]);

  return (
    <div className="flex min-h-[calc(100vh-7rem)] flex-col">
      <PageHeader title="Pergunte ao Clicou, Guardou!" />
      <div className="flex-1 space-y-4 px-5 pb-4" data-testid="ask-messages">
        {msgs.length === 0 && (
          <div className="ga-fade-up pt-6 text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-3xl" style={{ background: "#05324A" }}><Sparkles color="#33D9B2" size={28} /></div>
            <h2 className="mt-4 text-lg font-semibold">O que você quer lembrar?</h2>
            <p className="ga-muted mt-1 text-sm">Pergunte naturalmente. Respondo só com as suas memórias, {user?.name?.split(" ")[0]}.</p>
            <div className="mt-6 flex flex-wrap justify-center gap-2">
              {SUGGESTIONS.map((s) => <button key={s} onClick={() => ask(s)} className="ga-chip" data-testid="ask-suggestion-chip">{s}</button>)}
            </div>
          </div>
        )}
        {msgs.map((m, i) =>
          m.role === "user" ? (
            <div key={i} className="ga-fade-up flex justify-end"><div className="max-w-[85%] rounded-3xl rounded-br-md px-4 py-3 text-sm text-white" style={{ background: "#05324A" }} data-testid="ask-user-bubble">{m.text}</div></div>
          ) : (
            <div key={i} className="ga-fade-up space-y-2.5">
              <div className="flex items-start gap-2">
                <div className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full" style={{ background: "#13C9A3" }}><Sparkles size={14} color="#fff" /></div>
                <div className="ga-card max-w-[88%] rounded-3xl rounded-tl-md px-4 py-3 text-sm" data-testid="ask-answer-bubble">{m.answer}</div>
              </div>
              {m.memories?.length > 0 && (
                <div className="ml-9 space-y-2">
                  <p className="ga-muted text-[11px] font-semibold uppercase tracking-wide">Memória original</p>
                  {m.memories.map((mem) => (
                    <div key={mem.id}>
                      <MemoryCard memory={mem} compact />
                      <Link to={`/memorias/${mem.id}`} className="mt-1 inline-block text-xs font-semibold uppercase" style={{ color: "#0A6373" }} data-testid={`ask-view-original-${mem.id}`}>Ver memória original</Link>
                    </div>
                  ))}
                </div>
              )}
              {m.related?.length > 0 && (
                <div className="ml-9 space-y-2">
                  <p className="ga-muted text-[11px] font-semibold uppercase tracking-wide">Talvez relacionado</p>
                  {m.related.map((mem) => <MemoryCard key={mem.id} memory={mem} compact />)}
                </div>
              )}
              {m.follow_ups?.length > 0 && <div className="ml-9 flex flex-wrap gap-2">{m.follow_ups.map((f) => <button key={f} onClick={() => ask(f)} className="ga-chip" data-testid="ask-followup-chip">{f}</button>)}</div>}
            </div>
          )
        )}
        {busy && <div className="ml-9 flex gap-1.5 pl-1" data-testid="ask-loading"><span className="h-2 w-2 animate-bounce rounded-full bg-[#13C9A3]" /><span className="h-2 w-2 animate-bounce rounded-full bg-[#13C9A3] [animation-delay:120ms]" /><span className="h-2 w-2 animate-bounce rounded-full bg-[#13C9A3] [animation-delay:240ms]" /></div>}
        <div ref={endRef} />
      </div>
      <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="sticky bottom-24 px-5">
        <div className="ga-card flex items-center gap-2 p-1.5 pl-4 shadow-lg">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="O que você quer lembrar?" className="flex-1 bg-transparent text-sm outline-none" aria-label="Sua pergunta" data-testid="ask-guarda-ai-input" />
          <button type="submit" disabled={!q.trim() || busy} className="flex h-10 w-10 items-center justify-center rounded-full text-white disabled:opacity-40" style={{ background: "#13C9A3" }} aria-label="Enviar" data-testid="ask-guarda-ai-send"><Send size={18} /></button>
        </div>
      </form>
    </div>
  );
}
